import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code2, ArrowLeft, Calendar, CheckCircle, Clock, Users, ThumbsUp, MessageSquare, Zap, Target, Smartphone, Database, Bug, Rocket, TrendingUp, Play } from 'lucide-react';
import Button from '../../components/ui/Button';
import TimelineView from './components/TimelineView';
import CommunityFeedback from './components/CommunityFeedback';
import BetaProgram from './components/BetaProgram';
import CompletedFeatures from './components/CompletedFeatures';

const Roadmap = () => {
  const navigate = useNavigate();
  const [selectedQuarter, setSelectedQuarter] = useState('Q1 2025');
  const [view, setView] = useState('timeline'); // timeline, community, beta, completed

  const quarters = [
    {
      id: 'Q1 2025',
      name: 'Q1 2025',
      status: 'active',
      features: [
        {
          id: 1,
          title: 'Mobile Testing Missions',
          description: 'Learn mobile automation testing with Appium integration for iOS and Android applications.',
          status: 'in-progress',
          progress: 65,
          votes: 324,
          comments: 45,
          estimatedCompletion: '2025-03-15',
          category: 'Mobile Testing',
          icon: <Smartphone className="w-5 h-5" />
        },
        {
          id: 2,
          title: 'API Automation Challenges',
          description: 'Master REST API testing with automated validation scenarios and performance testing.',
          status: 'planning',
          progress: 25,
          votes: 298,
          comments: 38,
          estimatedCompletion: '2025-03-30',
          category: 'API Testing',
          icon: <Database className="w-5 h-5" />
        },
        {
          id: 3,
          title: 'Advanced Debugging Tools',
          description: 'Enhanced debugging interface with step-by-step execution, variable inspection, and error analysis.',
          status: 'in-progress',
          progress: 80,
          votes: 412,
          comments: 52,
          estimatedCompletion: '2025-02-28',
          category: 'Developer Tools',
          icon: <Bug className="w-5 h-5" />
        }
      ]
    },
    {
      id: 'Q2 2025',
      name: 'Q2 2025', 
      status: 'planned',
      features: [
        {
          id: 4,
          title: 'AI-Powered Test Generation',
          description: 'Automatically generate Selenium test cases from user interactions and business requirements.',
          status: 'planned',
          progress: 0,
          votes: 567,
          comments: 78,
          estimatedCompletion: '2025-06-15',
          category: 'AI Integration',
          icon: <Zap className="w-5 h-5" />
        },
        {
          id: 5,
          title: 'Team Collaboration Features',
          description: 'Share missions, collaborate on code, and track team progress with built-in collaboration tools.',
          status: 'planned',
          progress: 0,
          votes: 445,
          comments: 63,
          estimatedCompletion: '2025-05-30',
          category: 'Collaboration',
          icon: <Users className="w-5 h-5" />
        },
        {
          id: 6,
          title: 'Performance Testing Missions',
          description: 'Learn load testing, stress testing, and performance optimization techniques.',
          status: 'planned',
          progress: 0,
          votes: 356,
          comments: 41,
          estimatedCompletion: '2025-06-30',
          category: 'Performance',
          icon: <TrendingUp className="w-5 h-5" />
        }
      ]
    },
    {
      id: 'Q3 2025',
      name: 'Q3 2025',
      status: 'planned',
      features: [
        {
          id: 7,
          title: 'Multi-Browser Cloud Testing',
          description: 'Execute tests across multiple browsers and devices simultaneously in the cloud.',
          status: 'planned',
          progress: 0,
          votes: 423,
          comments: 55,
          estimatedCompletion: '2025-09-15',
          category: 'Cloud Testing',
          icon: <Target className="w-5 h-5" />
        },
        {
          id: 8,
          title: 'Advanced Reporting Dashboard',
          description: 'Comprehensive analytics and reporting with custom metrics and exportable reports.',
          status: 'planned',
          progress: 0,
          votes: 234,
          comments: 29,
          estimatedCompletion: '2025-08-30',
          category: 'Analytics',
          icon: <TrendingUp className="w-5 h-5" />
        }
      ]
    },
    {
      id: 'Q4 2025',
      name: 'Q4 2025',
      status: 'planned',
      features: [
        {
          id: 9,
          title: 'Enterprise Integration',
          description: 'SSO, LDAP integration, and enterprise-grade security features for organizations.',
          status: 'planned',
          progress: 0,
          votes: 198,
          comments: 22,
          estimatedCompletion: '2025-12-15',
          category: 'Enterprise',
          icon: <Rocket className="w-5 h-5" />
        }
      ]
    }
  ];

  const currentDevelopment = quarters?.find(q => q?.status === 'active')?.features?.filter(f => f?.status === 'in-progress') || [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Home</span>
              </Button>
              <div className="h-6 w-px bg-border"></div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Code2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold">Roadmap</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/user-login')}
              >
                Login
              </Button>
              <Button 
                size="sm"
                onClick={() => navigate('/user-registration')}
              >
                Start Learning
              </Button>
            </div>
          </div>
        </div>
      </nav>
      {/* Header */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <h1 className="text-4xl lg:text-5xl font-bold">
              Platform Development
              <span className="block bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                Roadmap
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Discover what's coming next to Selenium Sandbox. Vote on features, participate in beta testing, 
              and help shape the future of automation testing education.
            </p>
          </motion.div>
        </div>
      </section>
      {/* View Tabs */}
      <section className="px-4 sm:px-6 lg:px-8 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-4 justify-center">
            {[
              { id: 'timeline', name: 'Timeline View', icon: <Calendar className="w-4 h-4" /> },
              { id: 'community', name: 'Community Feedback', icon: <Users className="w-4 h-4" /> },
              { id: 'beta', name: 'Beta Program', icon: <Rocket className="w-4 h-4" /> },
              { id: 'completed', name: 'Recently Completed', icon: <CheckCircle className="w-4 h-4" /> }
            ]?.map((tab) => (
              <Button
                key={tab?.id}
                variant={view === tab?.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView(tab?.id)}
                className="flex items-center space-x-2"
              >
                {tab?.icon}
                <span>{tab?.name}</span>
              </Button>
            ))}
          </div>
        </div>
      </section>
      {/* Current Development Status */}
      {view === 'timeline' && (
        <section className="px-4 sm:px-6 lg:px-8 mb-12">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">Currently in Development</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentDevelopment?.map((feature, index) => (
                <motion.div
                  key={feature?.id}
                  className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
                        {feature?.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold">{feature?.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature?.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-green-500 text-sm font-medium">Active</span>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                    {feature?.description}
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span>Progress</span>
                      <span className="font-medium">{feature?.progress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${feature?.progress}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <ThumbsUp className="w-4 h-4" />
                          <span>{feature?.votes}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MessageSquare className="w-4 h-4" />
                          <span>{feature?.comments}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{new Date(feature.estimatedCompletion)?.toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* Main Content Based on Selected View */}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {view === 'timeline' && <TimelineView quarters={quarters} selectedQuarter={selectedQuarter} onQuarterSelect={setSelectedQuarter} />}
          {view === 'community' && <CommunityFeedback />}
          {view === 'beta' && <BetaProgram />}
          {view === 'completed' && <CompletedFeatures />}
        </div>
      </div>
      {/* Long-term Vision */}
      {view === 'timeline' && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/5 mt-12">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-8">Our Long-term Vision</h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Beyond 2025, we envision Selenium Sandbox evolving into the comprehensive test automation 
                  education platform, expanding beyond web testing to include mobile, API, performance, 
                  and accessibility testing.
                </p>
                <p>
                  Our goal is to create a complete ecosystem where learners can master all aspects of 
                  modern testing, from individual practice to enterprise-level collaboration and 
                  AI-assisted test generation.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Complete Testing Education</h3>
                  <p className="text-muted-foreground">Web, Mobile, API, Performance, and Accessibility testing all in one platform.</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Global Community</h3>
                  <p className="text-muted-foreground">Connect learners, mentors, and industry experts worldwide.</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mb-4">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">AI-Powered Learning</h3>
                  <p className="text-muted-foreground">Personalized learning paths with AI assistance and automatic test generation.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}
      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-8 text-white"
          >
            <h2 className="text-3xl font-bold mb-4">Help Shape Our Future</h2>
            <p className="text-xl mb-8 opacity-90">
              Your feedback and participation drive our development. Join our community and be part of the journey.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-white text-blue-600 hover:bg-white/90 border-white"
                onClick={() => setView('community')}
              >
                <MessageSquare className="mr-2 w-5 h-5" />
                Join Discussion
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-white text-blue-600 hover:bg-white/90 border-white"
                onClick={() => setView('beta')}
              >
                <Rocket className="mr-2 w-5 h-5" />
                Beta Program
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-white text-blue-600 hover:bg-white/90 border-white"
                onClick={() => navigate('/user-registration')}
              >
                <Play className="mr-2 w-5 h-5" />
                Start Learning
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Roadmap;